import { Button, Offcanvas } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import spinnerTitle from 'components/others/spinnerTitle';
import { useState } from 'react';
import SvgSave from 'components/svg/SvgSave';
import * as apiLead from 'api/apiLeads';
import * as apiObject from 'api/apiObjects';
import { useToast } from 'context/ToastContext';
import vconst from 'utils/helpers/structs/vconst';
import { useNavigate } from 'react-router-dom';
import { useSettings } from 'context/SettingsContext';
import type { LeadProposalGenerationType } from '@econtractor/types';
import { useNeedPay } from 'modules/admin';
import { syncNewObjectPriceFromProposal } from 'modules/lead';
import { getObjectCardPath } from 'utils/helpers/objects/objectCardPath';
import NewSaleForm from './NewSaleForm';
import type { NewSaleType } from './types';
import { useEffect } from 'react';

export type { NewSaleType } from './types';

export interface OffNewSaleProps {
    newSale: NewSaleType | null;
    setNewSale: React.Dispatch<React.SetStateAction<NewSaleType | null>>;
    acceptedGeneration?: LeadProposalGenerationType | null;
    acceptedLoading?: boolean;
    onRefreshAccepted?: () => void | Promise<void>;
}

const OffNewSale = ({
    newSale,
    setNewSale,
    acceptedGeneration = null,
    acceptedLoading = false,
    onRefreshAccepted,
}: OffNewSaleProps) => {
    const { t } = useTranslation(['Objects', 'common']);
    const { showToast } = useToast();
    const { getSettings, localSettings } = useSettings();
    const [creating, setCreating] = useState(false);
    const navigate = useNavigate();
    const { needPay } = useNeedPay();
    const leadId = Number(newSale?.leadId ?? 0);

    const rate = Number(localSettings?.RATE?.current ?? 1) || 1;

    useEffect(() => {
        if (!leadId || !acceptedGeneration || !newSale) return;
        setNewSale((prev) => {
            if (!prev) return null;
            return syncNewObjectPriceFromProposal(prev, acceptedGeneration, rate);
        });
    }, [acceptedGeneration, leadId, rate, setNewSale]);

    const handleClose = () => setNewSale(null);

    const canSave =
        Boolean(newSale)
        && Boolean((newSale?.objectName || '').trim());

    const handleClickCreate = async () => {
        if (!newSale) return;
        if (needPay()) return;
        setCreating(true);

        if (leadId > 0) {
            const eventData = {
                eventInfo: newSale?.info || '',
                statusID: vconst.leadAccept,
            };
            const resEvent = await apiLead.addEvent(leadId, eventData);
            if (!resEvent) {
                showToast(t('common:messError'), 'danger');
                setCreating(false);
                return;
            }
        }

        const payload = {
            ...newSale,
            onlySale: true,
            objectName: (newSale.objectName || '').trim(),
        };
        const resObject = await apiObject.createObject(payload as any);

        await getSettings();
        setCreating(false);

        if (resObject) {
            showToast(t('messSaleCreated'), 'success');
            setNewSale(null);
            navigate(getObjectCardPath(resObject, true));
        } else {
            showToast(t('common:messError'), 'danger');
        }
    };

    return (
        <Offcanvas
            className="off-import"
            show={newSale !== null}
            onHide={handleClose}
            onShow={() => {
                setCreating(false);
                void onRefreshAccepted?.();
            }}
            placement="end"
        >
            <Offcanvas.Header closeButton className="border-bottom">
                <Offcanvas.Title>{t('titleSale')}</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
                {newSale ? (
                    <NewSaleForm
                        newSale={newSale}
                        setNewSale={setNewSale}
                        hasActiveProposal={Boolean(acceptedGeneration)}
                        acceptedLoading={acceptedLoading}
                    />
                ) : null}
            </Offcanvas.Body>
            <div className="border-top p-3 d-flex justify-content-end gap-2">
                <Button variant="secondary" onClick={handleClose}>
                    {t('common:buttons.close')}
                </Button>
                <Button
                    variant="info"
                    disabled={!canSave || creating}
                    onClick={handleClickCreate}
                >
                    {spinnerTitle(
                        <>
                            <SvgSave className="me-2 mtop-2" />
                            {t('common:buttons.save')}
                        </>,
                        creating,
                        t('common:buttons.saving'),
                    )}
                </Button>
            </div>
        </Offcanvas>
    );
};

export default OffNewSale;
